import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useGameStore } from "../../store/gameStore";
import { Send, Lock, MapPin } from "lucide-react";

export function RollBubble({ msg }) {
  const b = msg.roll_breakdown;
  if (!b) return null;
  return (
    <div className={`mt-1 text-xs bg-black/40 rounded-lg px-3 py-2 border ${msg.is_private ? "border-[#D4AF37]/50" : "border-[#D4AF37]/20"}`} data-testid={`roll-breakdown-${msg.id}`}>
      <div className="flex items-baseline gap-2">
        <span className="font-mono-sheet text-lg text-[#D4AF37] font-semibold">{b.total}</span>
        <span className="text-[#f1e0c5]/50">
          {b.manual
            ? "GM manual override"
            : b.die !== undefined
            ? `d20 roll: ${b.rolls?.join(", ")} + ${b.modifier}`
            : `${b.formula || ""} → ${b.rolls?.join(" + ")}${b.modifier ? ` + ${b.modifier}` : ""}`}
        </span>
      </div>
      {b.bonuses?.length > 0 && (
        <div className="text-[#f1e0c5]/40 mt-1">
          {b.bonuses.map((bo, i) => (
            <span key={i} className="mr-2">
              {bo.label}: {bo.value >= 0 ? "+" : ""}{bo.value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChatPanel({ gameId, isGm, myMemberId, members }) {
  const chatMessages = useGameStore((s) => s.chatMessages);
  const fetchChat = useGameStore((s) => s.fetchChat);
  const freeRoam = useGameStore((s) => s.free_roam);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const inCombatMemberIds = new Set((freeRoam?.encounters || []).flatMap((e) => (e.order || []).map((o) => o.member_id).filter(Boolean)));

  useEffect(() => {
    fetchChat(gameId);
  }, [gameId, fetchChat]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [chatMessages]);

  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/games/${gameId}/chat`, { content: text });
    setText("");
  };

  const targetName = (id) => (members || []).find((m) => m.id === id)?.display_name;

  // Defense-in-depth: never render a whisper, a private/secret roll, or a Free Roam proximity
  // chat message that was out of earshot, on screen even if one slipped through (backend already
  // filters all three server-side).
  const visibleMessages = chatMessages.filter((m) => {
    if (m.type === "whisper") return false;
    if (m.is_private && !isGm && m.sender_member_id !== myMemberId && m.whisper_target_member_id !== myMemberId) return false;
    if (m.audible_member_ids && !isGm && m.sender_member_id !== myMemberId && !m.audible_member_ids.includes(myMemberId)) return false;
    return true;
  });

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-3">
        {visibleMessages.map((m) => (
          <div key={m.id} data-testid={`chat-message-${m.id}`}>
            <div className="text-xs flex items-center gap-1.5">
              <span className={`font-semibold ${inCombatMemberIds.has(m.sender_member_id) ? "text-[#D90429]" : "text-[#D4AF37]"}`} data-testid={inCombatMemberIds.has(m.sender_member_id) ? `chat-name-glowing-${m.sender_member_id}` : undefined}>{m.sender_name}</span>
              {m.type === "roll" && <span className="text-[#f1e0c5]/40"> rolls {m.content}</span>}
              {m.is_private && (
                <span data-testid={`private-tag-${m.id}`} className="flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/30">
                  <Lock className="w-2.5 h-2.5" /> {m.whisper_target_member_id ? `Secret → ${targetName(m.whisper_target_member_id) || "player"}` : "Private"}
                </span>
              )}
              {m.audible_member_ids && (
                <span data-testid={`proximity-tag-${m.id}`} title="Only heard by whoever was standing nearby" className="flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-teal-500/15 text-teal-300 border border-teal-400/30">
                  <MapPin className="w-2.5 h-2.5" /> Nearby
                </span>
              )}
            </div>
            {m.type === "chat" ? (
              <p className="text-sm text-[#f1e0c5]/85 mt-0.5">{m.content}</p>
            ) : (
              <RollBubble msg={m} />
            )}
          </div>
        ))}
        {visibleMessages.length === 0 && <p className="text-sm text-[#f1e0c5]/30 text-center pt-10">No messages yet. Say hello!</p>}
      </div>
      <div className="flex gap-2 p-3 border-t border-white/10">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Send a message…"
          className="flex-1 bg-black/30 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:ring-2 focus:ring-[#D90429]"
        />
        <button data-testid="chat-send-button" onClick={send} className="w-10 h-10 rounded-full bg-[#D90429] hover:bg-[#b8031f] flex items-center justify-center transition-colors duration-200">
          <Send className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}
